import { BaseElement } from '../../base-element';
import { Signal } from 'signal-polyfill';
import { secretaryApi } from '../../../api/secretary.api';
import { trialsApi } from '../../../api/trials.api';
import type { EntrySummaryDto, EntryStatus, PdfStatus, TrialEventSummaryDto } from '../../../api/types';
import { listActions, listState, totalPages } from './secretary-state';
import { authState, canAccessSecretaryFeatures } from '../../../state/auth.state';
import { navigate } from '../../../router';
import './components/entry-filters';
import './components/entry-table';
import '../../ui/dt-card';
import '../../ui/dt-button';
import '../../ui/dt-spinner';
import '../../ui/dt-select';

type PageState = 'loading' | 'loaded' | 'empty' | 'error' | 'forbidden';

interface FiltersChangeDetail {
  trialId: string | null;
  status: EntryStatus | 'All';
  pdfStatus: PdfStatus | 'All';
  search: string;
}

type EntryFiltersElement = HTMLElement & {
  trials: TrialEventSummaryDto[];
  filters: FiltersChangeDetail;
  fixedTrialId: string | null;
};

type EntryTableElement = HTMLElement & {
  entries: EntrySummaryDto[];
};

type DtButtonElement = HTMLElement & {
  disabled?: boolean;
  loading?: boolean;
};

type DtSelectElement = HTMLElement & {
  options: Array<{ value: string; label: string }>;
  value: string;
  disabled?: boolean;
};

export class SecretaryEntryListView extends BaseElement {
  private fixedTrialEventId = '';
  private trialName = '';
  private trials = new Signal.State<TrialEventSummaryDto[]>([]);
  private error = new Signal.State<string | null>(null);
  private initialized = false;
  private exporting = new Signal.State(false);

  private pageState = new Signal.Computed<PageState>(() => {
    const auth = authState.get();
    if (!auth.isAuthenticated || !canAccessSecretaryFeatures.get()) {
      return 'forbidden';
    }

    if (listState.get().loading) return 'loading';
    if (this.error.get()) return 'error';
    if (listState.get().entries.length === 0) return 'empty';
    return 'loaded';
  });

  static get observedAttributes(): string[] {
    return ['trial-event-id', 'trial-name'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'trial-event-id') {
      this.fixedTrialEventId = newValue || '';
      if (this.fixedTrialEventId) {
        listActions.setFilters({ trialId: this.fixedTrialEventId });
      }
    }

    if (name === 'trial-name') {
      this.trialName = newValue || '';
    }

    if (this.isConnected) {
      this.updateRender();
    }
  }

  connectedCallback(): void {
    this.watch(listState, () => this.updateRender());
    this.watch(this.trials, () => this.updateRender());
    this.watch(this.error, () => this.updateRender());
    this.watch(this.exporting, () => this.updateRender());
    this.watch(canAccessSecretaryFeatures, () => this.ensureData());
    this.watch(authState, () => this.ensureData());

    if (this.fixedTrialEventId) {
      listActions.setFilters({ trialId: this.fixedTrialEventId });
    }

    this.updateRender();
    this.ensureData();
  }

  private canAccess(): boolean {
    const auth = authState.get();
    return auth.isAuthenticated && canAccessSecretaryFeatures.get();
  }

  private ensureData(): void {
    if (!this.canAccess() || this.initialized) {
      return;
    }

    this.initialized = true;
    void this.loadTrials();
    void this.loadEntries();
  }

  private async loadTrials(): Promise<void> {
    if (this.fixedTrialEventId && this.trialName) {
      this.trials.set([
        {
          trialEventId: this.fixedTrialEventId,
          name: this.trialName,
          formTemplate: {
            organizationCode: '',
            sportCode: '',
            formCode: '',
            version: '',
          },
          clubSlug: '',
          eventSlug: '',
          trackingSlug: '',
          hostClubName: '',
          startDate: '',
          endDate: '',
          location: null,
          secretaryEmail: '',
          isActive: true,
        },
      ]);
      return;
    }

    const response = await trialsApi.list();
    if (response.error || !response.data) {
      this.trials.set([]);
      return;
    }

    this.trials.set(response.data);
  }

  private async loadEntries(): Promise<void> {
    if (!this.canAccess()) return;

    const state = listState.get();
    listActions.setLoading(true);
    this.error.set(null);

    const trialEventId = this.fixedTrialEventId || state.filters.trialId || undefined;

    const response = await secretaryApi.getEntries({
      page: state.page,
      pageSize: state.pageSize,
      trialEventId,
      status: state.filters.status,
      pdfStatus: state.filters.pdfStatus,
      search: state.filters.search.trim() || undefined,
    });

    if (response.error) {
      this.error.set('Unable to load entries. Please try again.');
      listActions.setEntries([], 0);
      listActions.setLoading(false);
      return;
    }

    const data = response.data;
    listActions.setEntries(
      data?.items ?? [],
      data?.total ?? 0,
      data?.page ?? state.page,
      data?.pageSize ?? state.pageSize
    );
    listActions.setLoading(false);
  }

  private handleBackToTrials = (): void => {
    navigate('/secretary');
  };

  private handleFiltersChange = (event: Event): void => {
    const detail = (event as CustomEvent<FiltersChangeDetail>).detail;
    if (!detail) return;

    listActions.setFilters({
      trialId: this.fixedTrialEventId || detail.trialId,
      status: detail.status,
      pdfStatus: detail.pdfStatus,
      search: detail.search,
    });
    void this.loadEntries();
  };

  private handlePrevPage = (): void => {
    const state = listState.get();
    if (state.page <= 1) return;
    listActions.setPage(state.page - 1);
    void this.loadEntries();
  };

  private handleNextPage = (): void => {
    const state = listState.get();
    const pages = totalPages.get();
    if (state.page >= pages) return;
    listActions.setPage(state.page + 1);
    void this.loadEntries();
  };

  private handlePageSizeChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    const nextSize = Number(detail?.value);
    if (!Number.isNaN(nextSize) && nextSize > 0) {
      listActions.setPageSize(nextSize);
      void this.loadEntries();
    }
  };

  private handleRetry = (): void => {
    void this.loadEntries();
  };

  private handleEntrySelected = (event: Event): void => {
    const detail = (event as CustomEvent<{ entryId: string; trialEventId: string }>).detail;
    if (!detail?.entryId || !detail?.trialEventId) return;
    navigate(`/secretary/entries/${detail.trialEventId}/${detail.entryId}`);
  };

  private async handleExport(format: 'csv' | 'xlsx'): Promise<void> {
    const trialId = this.fixedTrialEventId || listState.get().filters.trialId;
    if (!trialId) return;

    this.exporting.set(true);
    try {
      await secretaryApi.exportEntries(trialId, format);
    } catch {
      // toast is handled by the API client
    } finally {
      this.exporting.set(false);
      this.updateRender();
    }
  }

  private renderLoading(): string {
    return `
      <div class="flex min-h-[240px] flex-col items-center justify-center gap-3" data-testid="loading-state">
        <dt-spinner size="large"></dt-spinner>
        <p class="text-sm text-neutral-600">Loading entries...</p>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="flex min-h-[240px] flex-col items-center justify-center gap-4" data-testid="error-state">
        <div class="rounded-2xl border border-error-200 bg-error-50 p-6 text-center">
          <h2 class="text-lg font-semibold text-error-700">Unable to load entries</h2>
          <p class="mt-2 text-sm text-error-600">${message}</p>
          <dt-button variant="outline" class="mt-4" data-testid="retry-btn">Retry</dt-button>
        </div>
      </div>
    `;
  }

  private renderEmpty(): string {
    return `
      <div class="flex min-h-[200px] items-center justify-center" data-testid="empty-state">
        <p class="text-sm text-neutral-500">No entries found.</p>
      </div>
    `;
  }

  private renderPagination(): string {
    const state = listState.get();
    const pages = totalPages.get();

    if (state.total <= state.pageSize) {
      return '';
    }

    const start = state.total === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
    const end = Math.min(state.page * state.pageSize, state.total);

    return `
      <div class="flex flex-wrap items-center justify-between gap-4 border-t border-neutral-200 px-4 py-4" data-testid="pagination">
        <div class="text-xs text-neutral-500">Showing ${start}-${end} of ${state.total}</div>
        <div class="flex flex-wrap items-center gap-3">
          <dt-button variant="outline" data-testid="prev-page">Previous</dt-button>
          <span class="text-xs font-semibold text-neutral-600">Page ${state.page} of ${pages}</span>
          <dt-button variant="outline" data-testid="next-page">Next</dt-button>
        </div>
        <div class="min-w-[120px]">
          <dt-select label="Page size" data-testid="page-size"></dt-select>
        </div>
      </div>
    `;
  }

  private renderContent(): string {
    const pageState = this.pageState.get();
    let body = '';

    if (pageState === 'loading') {
      body = this.renderLoading();
    } else if (pageState === 'error') {
      body = this.renderError(this.error.get() ?? 'Unable to load.');
    } else if (pageState === 'empty') {
      body = this.renderEmpty();
    } else if (pageState === 'loaded') {
      body = `
        <dt-entry-table data-testid="entries-table"></dt-entry-table>
        ${this.renderPagination()}
      `;
    }

    const hasTrialSelected = !!(this.fixedTrialEventId || listState.get().filters.trialId);
    const isExporting = this.exporting.get();
    const exportDisabled = !hasTrialSelected || isExporting;

    return `
      <section class="px-4 py-6" data-testid="secretary-entry-view">
        <div class="mx-auto flex w-full max-w-6xl flex-col gap-6">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <dt-button variant="ghost" data-testid="back-to-trials">Back to trial events</dt-button>
            <div class="text-xs uppercase tracking-[0.12em] text-neutral-500">Entries</div>
          </div>

          <dt-card variant="outlined">
            <div slot="header">${this.trialName ? this.esc(this.trialName) : 'Entry Management'}</div>
            <div class="flex items-center justify-between gap-3">
              <div class="text-sm text-neutral-600">Review submitted entries, PDF processing, and entry details for this trial event.</div>
              <div class="relative inline-block" data-testid="export-btn">
                <dt-button variant="outline" ${exportDisabled ? 'disabled' : ''} data-testid="export-toggle">
                  ${isExporting ? 'Exporting…' : 'Export ▾'}
                </dt-button>
                <div class="export-dropdown absolute right-0 z-10 mt-1 hidden w-36 rounded border border-neutral-200 bg-white shadow-lg">
                  <button class="block w-full px-4 py-2 text-left text-sm hover:bg-neutral-100" data-export-format="csv">Export CSV</button>
                  <button class="block w-full px-4 py-2 text-left text-sm hover:bg-neutral-100" data-export-format="xlsx">Export XLSX</button>
                </div>
              </div>
            </div>
          </dt-card>

          <dt-entry-filters></dt-entry-filters>
          <div class="flex flex-col gap-4">${body}</div>
        </div>
      </section>
    `;
  }

  private renderForbidden(): string {
    return `
      <section class="px-4 py-12" data-testid="forbidden-state">
        <div class="mx-auto max-w-xl text-center">
          <h2 class="text-xl font-semibold text-neutral-900">Access restricted</h2>
          <p class="mt-2 text-sm text-neutral-600">You do not have permission to view the entry management workflow.</p>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    const state = this.pageState.get();
    const content = state === 'forbidden' ? this.renderForbidden() : this.renderContent();

    this.render(content);
    this.attachEventListeners();
    this.assignData();
  }

  private attachEventListeners(): void {
    const backBtn = this.$('[data-testid="back-to-trials"]');
    if (backBtn) {
      backBtn.addEventListener('click', this.handleBackToTrials);
    }

    const filters = this.$('dt-entry-filters');
    if (filters) {
      filters.addEventListener('filters-change', this.handleFiltersChange);
    }

    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', this.handleRetry);
    }

    const table = this.$('dt-entry-table');
    if (table) {
      table.addEventListener('entry-selected', this.handleEntrySelected);
    }

    const exportToggle = this.$('[data-testid="export-toggle"]');
    const exportDropdown = this.$('.export-dropdown');
    if (exportToggle && exportDropdown) {
      exportToggle.addEventListener('click', () => {
        exportDropdown.classList.toggle('hidden');
      });
    }

    const csvBtn = this.$('[data-export-format="csv"]');
    if (csvBtn) {
      csvBtn.addEventListener('click', () => {
        exportDropdown?.classList.add('hidden');
        void this.handleExport('csv');
      });
    }

    const xlsxBtn = this.$('[data-export-format="xlsx"]');
    if (xlsxBtn) {
      xlsxBtn.addEventListener('click', () => {
        exportDropdown?.classList.add('hidden');
        void this.handleExport('xlsx');
      });
    }

    const prevBtn = this.$('[data-testid="prev-page"]');
    if (prevBtn) {
      prevBtn.addEventListener('click', this.handlePrevPage);
    }

    const nextBtn = this.$('[data-testid="next-page"]');
    if (nextBtn) {
      nextBtn.addEventListener('click', this.handleNextPage);
    }

    const pageSizeSelect = this.$('[data-testid="page-size"]') as DtSelectElement | null;
    if (pageSizeSelect) {
      pageSizeSelect.addEventListener('change', this.handlePageSizeChange);
    }
  }

  private assignData(): void {
    if (this.pageState.get() === 'forbidden') return;

    const filters = this.$('dt-entry-filters') as EntryFiltersElement | null;
    if (filters) {
      filters.trials = this.trials.get();
      filters.filters = {
        ...listState.get().filters,
        trialId: this.fixedTrialEventId || listState.get().filters.trialId,
      };
      filters.fixedTrialId = this.fixedTrialEventId || null;
    }

    const table = this.$('dt-entry-table') as EntryTableElement | null;
    if (table) {
      table.entries = listState.get().entries;
    }

    const prevBtn = this.$('[data-testid="prev-page"]') as DtButtonElement | null;
    const nextBtn = this.$('[data-testid="next-page"]') as DtButtonElement | null;
    const pageState = listState.get();
    if (prevBtn) {
      prevBtn.disabled = pageState.page <= 1 || pageState.loading;
    }
    if (nextBtn) {
      nextBtn.disabled = pageState.page >= totalPages.get() || pageState.loading;
    }

    const pageSizeSelect = this.$('[data-testid="page-size"]') as DtSelectElement | null;
    if (pageSizeSelect) {
      pageSizeSelect.options = [
        { value: '10', label: '10' },
        { value: '25', label: '25' },
        { value: '50', label: '50' },
      ];
      pageSizeSelect.value = pageState.pageSize.toString();
      pageSizeSelect.disabled = pageState.loading;
    }
  }
}

customElements.define('dt-secretary-entry-list-view', SecretaryEntryListView);